import { useState, useRef, useEffect } from 'react';
import { Eye, X, AlertCircle } from 'lucide-react';

/**
 * ShopperImageExtract - Displays cropped shopper image extract with modal expansion
 * 
 * @param {string} imageUrl - Full shopper page image URL
 * @param {object} rect - Rectangle coordinates { x, y, w, h } (normalized 0-1)
 * @param {object} metadata - Full meta object (includes imgW, imgH, version)
 * @param {number} pageNumber - Page number for alt text
 */
export default function ShopperImageExtract({ imageUrl, rect, metadata, pageNumber }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const canvasRef = useRef(null);
  const modalImageRef = useRef(null);

  // Handle missing data
  if (!imageUrl || !rect) {
    return null;
  }

  // Validate URL protocol (security: only allow HTTPS)
  try {
    const url = new URL(imageUrl);
    if (url.protocol !== 'https:') {
      console.warn('[ShopperImageExtract] Blocked non-HTTPS URL:', imageUrl);
      return null;
    }
  } catch {
    console.warn('[ShopperImageExtract] Invalid URL:', imageUrl);
    return null;
  }

  // Calculate cropped region for thumbnail (with 20% padding)
  const padding = 0.2;
  const cropX = Math.max(0, rect.x - padding * rect.w);
  const cropY = Math.max(0, rect.y - padding * rect.h);
  const cropW = Math.min(1, rect.w * (1 + 2 * padding));
  const cropH = Math.min(1, rect.h * (1 + 2 * padding));

  // CSS object-position uses percentages
  const objectPosition = `${cropX * 100}% ${cropY * 100}%`;

  // Draw rectangle overlay on canvas
  const drawRectangleOverlay = () => {
    const canvas = canvasRef.current;
    const img = modalImageRef.current;
    
    if (!canvas || !img || !img.complete) return;

    const ctx = canvas.getContext('2d');
    
    // Set canvas size to match displayed image
    const imgBounds = img.getBoundingClientRect();
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.style.width = `${imgBounds.width}px`;
    canvas.style.height = `${imgBounds.height}px`;

    // Draw rectangle using normalized coordinates (from rect prop)
    // rect prop contains { x, y, w, h } normalized 0-1 relative to natural image
    const rectX = rect.x * img.naturalWidth;
    const rectY = rect.y * img.naturalHeight;
    const rectW = rect.w * img.naturalWidth;
    const rectH = rect.h * img.naturalHeight;

    // Fill with semi-transparent yellow (stands out against warm colors)
    ctx.fillStyle = 'rgba(234, 179, 8, 0.25)';
    ctx.fillRect(rectX, rectY, rectW, rectH);

    // Stroke with solid bright yellow, thick border for visibility
    ctx.strokeStyle = 'rgb(234, 179, 8)';
    ctx.lineWidth = 6;
    ctx.strokeRect(rectX, rectY, rectW, rectH);
  };

  // Handle modal image load
  const handleModalImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
    drawRectangleOverlay();
  };

  // Handle image error
  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  // Open modal
  const openModal = () => {
    setIsModalOpen(true);
    setIsLoading(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Handle ESC key
  useEffect(() => {
    if (!isModalOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') closeModal();
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isModalOpen]);

  // Redraw on window resize (if modal is open)
  useEffect(() => {
    if (!isModalOpen) return;

    const handleResize = () => {
      if (modalImageRef.current?.complete) {
        drawRectangleOverlay();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isModalOpen]);

  return (
    <>
      {/* Thumbnail with Expand Button */}
      <div className="flex items-start gap-3 mt-2">
        {/* Cropped Thumbnail */}
        <div className="flex-shrink-0">
          <img
            src={imageUrl}
            alt={`Missing spot marked on page ${pageNumber ?? 'unknown'}`}
            className="w-[150px] h-[150px] object-cover rounded-md border border-neutral-200 shadow-sm hover:border-primary-300 transition-colors cursor-pointer"
            style={{ objectPosition }}
            onClick={openModal}
            onError={handleImageError}
          />
        </div>

        {/* Expand Button */}
        <button
          onClick={openModal}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-100 rounded-md transition-colors"
          aria-label="View full shopper page with marked area"
        >
          <Eye size={14} />
          <span>Expand Image</span>
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/80 p-4"
          onClick={closeModal}
        >
          {/* Modal Content */}
          <div
            className="relative bg-white rounded-lg shadow-lg p-6 max-w-4xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 p-2 hover:bg-neutral-100 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>

            {/* Title */}
            <h3 className="text-lg font-semibold text-neutral-900 mb-4 pr-10">
              Missing Spot — Page {pageNumber ?? 'Unknown'}
            </h3>

            {/* Image Container with Canvas Overlay */}
            <div className="relative inline-block">
              {/* Loading State */}
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-neutral-100 rounded">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              )}

              {/* Error State */}
              {hasError && (
                <div className="flex items-center gap-2 text-error-600 bg-error-50 px-4 py-3 rounded-md">
                  <AlertCircle size={20} />
                  <span>Failed to load image</span>
                </div>
              )}

              {/* Full Image */}
              {!hasError && (
                <>
                  <img
                    ref={modalImageRef}
                    src={imageUrl}
                    alt={`Full shopper page ${pageNumber ?? 'unknown'}`}
                    className="max-w-full max-h-[70vh] object-contain"
                    onLoad={handleModalImageLoad}
                    onError={handleImageError}
                  />
                  
                  {/* Canvas Overlay (positioned absolute over image) */}
                  <canvas
                    ref={canvasRef}
                    className="absolute top-0 left-0 pointer-events-none"
                  />
                </>
              )}
            </div>

            {/* Metadata (optional debug info) */}
            {metadata?.version && (
              <p className="text-xs text-neutral-500 mt-4">
                Metadata version: {metadata.version}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
